
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ExternalLink } from 'lucide-react';

interface PortfolioItemProps {
  title: string;
  description: string;
  image: string;
  link: string;
  tags?: string[];
}

const PortfolioItem: React.FC<PortfolioItemProps> = ({ title, description, image, link, tags }) => {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden transition-all hover:shadow-lg">
      <div className="relative">
        <AspectRatio ratio={16 / 9}>
          <img
            src={image}
            alt={title}
            className="object-cover w-full h-full rounded-t-lg"
          />
        </AspectRatio>
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl">{title}</CardTitle>
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((tag, index) => (
              <span 
                key={index}
                className="px-2 py-1 text-xs rounded-full bg-secondary text-secondary-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="pb-4">
        <CardDescription className="text-sm text-muted-foreground">
          {description}
        </CardDescription>
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" className="w-full" asChild>
          <a href={link} target="_blank" rel="noopener noreferrer">
            {t('portfolio.viewProject')} <ExternalLink className="ml-1 w-4 h-4" />
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
};

const Portfolio = () => {
  const { t } = useTranslation();
  
  const portfolioItems = [
    {
      title: t('portfolio.items.0.title'),
      description: t('portfolio.items.0.description'),
      image: t('portfolio.items.0.thumbnail', { returnObjects: true }) as String,
      link: "https://www.pausebonheur.com",
      tags: t('portfolio.items.0.tags', { returnObjects: true }) as string[]
    },
    {
      title: t('portfolio.items.1.title'),
      description: t('portfolio.items.1.description'),
      image: t('portfolio.items.1.thumbnail', { returnObjects: true }) as String,
      link: "https://www.therismos-global.com",
      tags: t('portfolio.items.1.tags', { returnObjects: true }) as string[]
    },
    {
      title: t('portfolio.items.2.title'),
      description: t('portfolio.items.2.description'),
      image: t('portfolio.items.2.thumbnail', { returnObjects: true }) as String,
      link: "https://tilecrack.com",
      tags: t('portfolio.items.2.tags', { returnObjects: true }) as string[]
    },
  ];

  return (
    <section id="portfolio" className="section bg-muted">
      <div className="container-custom">
        <div className="mb-10 text-center">
          <h2 className="mb-2 text-3xl font-bold md:text-4xl lg:text-5xl font-display">{t('portfolio.title')}</h2>
          <p className="max-w-2xl mx-auto text-muted-foreground">
            {t('portfolio.subtitle')}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {portfolioItems.map((item, index) => (
            <PortfolioItem key={index} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
